import {motion} from 'framer-motion';
import {ButtonGroup, closeCurrentDialog, DataStatus} from 'platform/components';
import {Box, Center, Grid, Text, VStack} from 'platform/foundation';

import {useListEmailProviderQuery} from '@dms/api/core';
import i18n from '@dms/i18n';

import {useBoolean} from 'shared/web';

import {providerNamesByCode} from '../constants/providerNamesByCode';
import {EmailServiceProvider} from '../types/EmailServiceProvider';
import {getSmtpProviderLogo} from '../utils/getSmtpProviderLogo';

export interface ServiceProviderProps {
  onSelect: (
    provider: EmailServiceProvider,
    startLoading: () => void,
    stopLoading: () => void
  ) => void;
}

export function ServiceProviders(props: ServiceProviderProps) {
  const {data: emailProviders} = useListEmailProviderQuery();
  const providers: EmailServiceProvider[] = emailProviders?.providers ?? [];

  const [isLoading, startLoading, stopLoading] = useBoolean();

  return (
    <VStack spacing={4}>
      <Grid columns={2} spacing={4}>
        {providers.map((provider) => {
          const logo = getSmtpProviderLogo(provider.code);
          const providerName = providerNamesByCode[provider.code];

          return (
            <motion.div
              key={provider.code}
              whileHover={{scale: 1.01}}
              role="button"
              tabIndex={0}
              onClick={() => props.onSelect(provider, startLoading, stopLoading)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  props.onSelect(provider, startLoading, stopLoading);
                }
              }}
              style={{cursor: 'pointer'}}
            >
              <Box
                transition="all 0.2s ease"
                borderRadius="medium"
                border="1px solid"
                borderColor="palettes.neutral.40.100"
                padding={6}
                backgroundColor="palettes.white.10.100"
                maxHeight={40}
              >
                <VStack align="center" justify="center" spacing={3}>
                  <Box height={20} width={20}>
                    <Center height="100%">{logo}</Center>
                  </Box>
                  <DataStatus isLoading={isLoading && provider.code === 'microsoft-oauth'}>
                    <Text color="secondary">{providerName}</Text>
                  </DataStatus>
                </VStack>
              </Box>
            </motion.div>
          );
        })}
      </Grid>
      <ButtonGroup
        align="right"
        buttons={[
          {
            title: i18n.t('general.actions.discard'),
            variant: 'secondary',
            onClick: closeCurrentDialog,
          },
        ]}
      />
    </VStack>
  );
}
